<template>
  <div class="fixed-assets-management pb-2">
    <b-card class="resources-list-card shadow-lg border-0">
      <!-- Gradient Line Wrapper -->
      <div class="gradient-line-wrapper"></div>
      
      <!-- Table Header -->
      <div class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2 px-1 pt-1">
        <div>
          <h5 class="mb-0 resource-title">Fixed Assets</h5>
          <small class="text-muted">Manage and track your organization's physical assets</small>
        </div>
        <div class="d-flex gap-2 align-items-center">

          <b-button v-ripple.400="'rgba(115, 103, 240, 0.15)'" variant="flat-primary" class="btn-icon rounded-circle" v-b-tooltip.hover title="Fiscal Year Management" @click="toggleFiscalYearSidebar(true)">
            <feather-icon icon="CalendarIcon" size="20" />
          </b-button>
          <b-button v-ripple.400="'rgba(115, 103, 240, 0.15)'" variant="flat-primary" class="btn-icon rounded-circle" v-b-tooltip.hover title="Parent Mapping" @click="toggleSettingsSidebar(true)">
            <feather-icon icon="SettingsIcon" size="20" />
          </b-button>
          <b-button v-if="rights.add" variant="gradient-primary" @click="openAddAssetSidebar" class="action-btn-filter shadow-primary hover-translate-y">
            <feather-icon icon="PlusIcon" size="16" class="mr-50" />
            Add Asset
          </b-button>
        </div>
      </div>

      <!-- Filter Row -->
      <b-row class="align-items-center filter-row-single-line mb-2 px-1">
        <b-col md="4">
          <b-form-input
            v-model="searchQuery"
            placeholder="Search by name or code..."
            class="filter-control bg-white shadow-sm"
            @input="onSearchChange"
            debounce="500"
          />
        </b-col>
      </b-row>

      <div class="pagination-controls-top d-flex justify-content-between align-items-center mb-2 mx-1 shadow-sm">
        <div class="d-flex align-items-center gap-2">
          <div class="page-size-wrapper">
            <label class="page-size-label">SHOW:</label>
            <v-select
              v-model="pageSize"
              :options="[10, 20, 50, 100]"
              :clearable="false"
              :searchable="false"
              class="page-size-select-compact"
              append-to-body
              @input="loadInitialData"
            />
          </div>
        </div>
        
        <div class="pagination-count-display">
          <span class="count-text">
            Showing <strong>{{ displayedRange.start }}</strong> to <strong>{{ displayedRange.end }}</strong> of <strong>{{ totalRecords }}</strong>
          </span>
        </div>
        
        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRecords"
            :per-page="pageSize"
            size="sm"
            align="center"
            class="pagination-compact mb-0"
            @change="loadInitialData"
          />
        </div>
      </div>

      <!-- Fluid Data Grid -->
      <div class="table-scroll-container">
        <b-table
          :items="assets"
          :fields="fields"
          :busy="dataLoading"
          hover
          responsive
          show-empty
          class="resources-table modern-table"
        >
          <template #table-busy>
            <div class="text-center text-primary my-4">
              <b-spinner class="align-middle mr-1" />
              <strong>Synchronizing assets...</strong>
            </div>
          </template>

          <template #empty>
            <div class="text-center py-5">
              <div class="empty-state-icon mb-1 mx-auto">
                <feather-icon icon="InboxIcon" size="60" class="text-muted opacity-25" />
              </div>
              <h4 class="text-secondary">No assets found</h4>
              <p class="text-muted font-small-3">Registered assets will appear here.</p>
            </div>
          </template>

          <template #cell(sno)="data">
            {{ (currentPage - 1) * pageSize + data.index + 1 }}
          </template>

          <template #cell(name)="data">
            <div class="d-flex align-items-center">
              <div class="asset-avatar mr-1 bg-light-primary rounded-circle d-flex align-items-center justify-content-center" style="width: 35px; height: 35px;">
                <feather-icon icon="PackageIcon" size="16" class="text-primary" />
              </div>
              <div class="title-cell-content">
                <span class="font-weight-bolder text-dark d-block">{{ data.value }}</span>
                <small class="text-muted tracking-wide">{{ data.item.assetCode || data.item.code }}</small>
              </div>
            </div>
          </template>

          <template #cell(nature)="data">
            <b-badge pill :variant="getNatureVariant(data.value)" class="text-uppercase font-small-1">
              {{ data.value }}
            </b-badge>
          </template>

          <template #cell(purchaseDate)="data">
            <span class="text-secondary font-weight-bold">{{ data.value | formatDate }}</span>
          </template>

          <template #cell(usefulLifeYears)="data">
            <b-badge variant="light-info" pill>{{ data.value }} yrs</b-badge>
          </template>

          <template #cell(actions)="{ item }">
            <div class="d-flex align-items-center justify-content-end">
              <b-button
                variant="gradient-primary"
                v-b-tooltip.hover.left
                title="View Details"
                size="sm"
                class="action-btn-table mr-1"
                @click.stop="viewAsset(item)"
              >
                <feather-icon icon="EyeIcon" size="16" />
              </b-button>

              <b-button
                variant="outline-warning"
                v-b-tooltip.hover.top
                title="Generate Depreciation"
                size="sm"
                class="action-btn-outline warning-btn mr-1"
                @click.stop="openDepreciationModal(item)"
              >
                <feather-icon icon="TrendingDownIcon" size="16" />
              </b-button>
              
              <b-button
                v-if="rights.edit"
                variant="outline-success"
                v-b-tooltip.hover.top
                title="Edit"
                size="sm"
                class="action-btn-outline edit-btn mr-1"
                @click.stop="editAsset(item)"
              >
                <feather-icon icon="EditIcon" size="16" />
              </b-button>
              
              <b-button
                v-if="rights.delete"
                variant="outline-danger"
                v-b-tooltip.hover.right
                title="Delete"
                size="sm"
                class="action-btn-outline delete-btn"
                @click.stop="confirmDelete(item)"
              >
                <feather-icon icon="Trash2Icon" size="16" />
              </b-button>
            </div>
          </template>
        </b-table>
      </div>
    </b-card>

    <!-- Global Account Settings Sidebar -->
    <b-sidebar
      id="settings-sidebar"
      v-model="isSettingsSidebarOpen"
      bg-variant="white"
      backdrop
      shadow
      width="420px"
      no-header
      right
      class="premium-sidebar"
    >
      <div class="sidebar-header-modern d-flex justify-content-between align-items-center p-2 border-bottom">
        <div class="d-flex align-items-center">
          <div class="sidebar-icon-box bg-light-primary mr-1">
            <feather-icon icon="SettingsIcon" size="20" class="text-primary" />
          </div>
          <h4 class="mb-0 font-weight-bolder">Parent Setting</h4>
        </div>
        <b-button variant="flat-secondary" class="btn-icon rounded-circle" @click="toggleSettingsSidebar(false)">
          <feather-icon icon="XIcon" size="18" />
        </b-button>
      </div>

      <div class="sidebar-body-modern p-2">
        <div class="mb-3 px-1">
          <h6 class="text-uppercase text-muted font-weight-bold mb-1 font-small-2">Configuration View</h6>
          <label class="font-small-4 font-weight-bold mb-50">Select Parent Account</label>
          <v-select 
            v-model="selectedAccountID" 
            :options="parentOptions" 
            :reduce="opt => opt.id" 
            label="title" 
            placeholder="Search account by title or code..."
            class="modern-select shadow-sm"
          >
            <template #option="option">
              <div class="d-flex justify-content-between align-items-center py-25">
                <div class="d-flex flex-column">
                  <span class="font-weight-bolder font-small-4">{{ option.title }}</span>
                  <small class="text-muted font-small-3">{{ option.code || option.assetCode }}</small>
                </div>
              </div>
            </template>
          </v-select>
        </div>

        <div v-if="selectedAccountID" class="px-1 animate__animated animate__fadeInUp">
          <div class="settings-card p-2 rounded-lg bg-light border border-light shadow-sm text-center">
            <div class="mb-2">
              <feather-icon icon="InfoIcon" size="24" class="text-primary mb-1" />
              <p class="font-weight-bold mb-0">Link Account to System Key</p>
              <small class="text-muted">This will set the selected account as the primary key for fixed asset mapping.</small>
            </div>
            
            <b-button variant="gradient-primary" block size="lg" class="mt-1 py-1 shadow-primary" @click="saveAccountSetting" :disabled="savingSetting">
              <b-spinner v-if="savingSetting" small class="mr-1" />
              <feather-icon v-else icon="LinkIcon" size="18" class="mr-50" />
              Confirm & Link Account
            </b-button>
          </div>
        </div>

        <div v-else class="text-center py-5 transition-all">
          <div class="pulsing-circle mb-2 mx-auto">
             <feather-icon icon="SearchIcon" size="32" class="text-muted" />
          </div>
          <h5 class="text-secondary font-weight-bold">Ready to Configure</h5>
          <p class="text-muted px-3 font-small-3">Search and select an account above to modify its financial properties.</p>
        </div>
      </div>
    </b-sidebar>

    <!-- Register New Asset Sidebar -->
    <b-sidebar
      id="add-asset-sidebar"
      v-model="isSidebarOpen"
      bg-variant="white"
      backdrop
      shadow
      width="650px"
      no-header
      right
      class="premium-sidebar"
    >
      <div class="sidebar-header-modern d-flex justify-content-between align-items-center p-2 border-bottom bg-light">
        <div class="d-flex align-items-center text-dark">
          <div class="sidebar-icon-box bg-white mr-1 shadow-sm">
            <feather-icon :icon="sidebarMode === 'add' ? 'PlusSquareIcon' : 'EditIcon'" size="20" class="text-primary" />
          </div>
          <h4 class="mb-0 font-weight-bolder text-dark tracking-tight">{{ sidebarTitle }}</h4>
        </div>
        <b-button variant="flat-secondary" class="btn-icon rounded-circle border-0" @click="toggleSidebar(false)">
          <feather-icon icon="XIcon" size="22" />
        </b-button>
      </div>

      <div class="sidebar-body-modern p-2 bg-light-faint">
        <b-form @submit.prevent="handleSaveAsset" class="py-1">
          <b-card no-body class="p-2 mb-2 shadow-sm border-0 animate__animated animate__fadeIn">
             <b-row>
               <!-- Row 1: Asset Name & Asset Code -->
               <b-col md="8" class="mb-2">
                 <label class="font-weight-bold text-dark mb-50">Asset Identification Name</label>
                 <b-form-input v-model="assetForm.name" placeholder="e.g. Dell XPS 15 (IT Dept)" class="form-control-lg border-primary-light" required />
               </b-col>
               <b-col md="4" class="mb-2">
                 <label class="font-weight-bold text-dark mb-50">Asset Code</label>
                  <b-input-group>
                    <b-form-input v-model="assetForm.assetCode" placeholder="FA-001" class="border-primary-light" :disabled="generatingCode" />
                    <b-input-group-append>
                      <b-button variant="outline-primary" class="px-1" @click="generateCode" :disabled="generatingCode">
                         <b-spinner v-if="generatingCode" small />
                         <feather-icon v-else icon="RefreshCwIcon" size="14" />
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                </b-col>

               <!-- Row 2: Category -->
               <b-col md="12" class="mb-2">
                 <div class="d-flex justify-content-between align-items-center mb-50">
                   <label class="font-weight-bold text-dark mb-0">Category</label>
                   <b-button 
                     variant="flat-primary" 
                     size="sm" 
                     class="btn-icon rounded-circle p-0" 
                     style="width: 30px; height: 30px;"
                     v-b-tooltip.hover 
                     title="Add/Edit Category"
                     @click="openEditCategoryModal"
                   >
                     <feather-icon icon="EditIcon" size="14" />
                   </b-button>
                 </div>
                 <v-select v-model="assetForm.categoryID" :options="categoryOptions" :reduce="opt => opt.id" label="name" placeholder="Select category" />
               </b-col>

               <!-- Row 3: Purchase & Capitalization Dates -->
               <b-col md="6" class="mb-2">
                 <label class="font-weight-bold text-dark mb-50">Purchase Date</label>
                 <flat-pickr v-model="assetForm.purchaseDate" :config="datePickerConfig" class="form-control" />
               </b-col>
               <b-col md="6" class="mb-2">
                 <label class="font-weight-bold text-dark mb-50">Capitalization Date</label>
                 <flat-pickr v-model="assetForm.capitalizationDate" :config="datePickerConfig" class="form-control" />
               </b-col>

               <!-- Row 4: Purchase Cost & Salvage Value -->
               <b-col md="6" class="mb-2">
                 <label class="font-weight-bold text-dark mb-50">Purchase Cost</label>
                 <b-form-input type="number" v-model="assetForm.purchaseCost" class="form-control text-primary font-weight-bold" />
               </b-col>
               <b-col md="6" class="mb-2">
                 <label class="font-weight-bold text-dark mb-50">Salvage Value</label>
                 <b-form-input type="number" v-model="assetForm.salvageValue" class="form-control" />
               </b-col>

               <!-- Row 5: Useful Life & Depreciation Method -->
               <b-col md="6" class="mb-2">
                 <label class="font-weight-bold text-dark mb-50">Useful Life (Years)</label>
                 <b-form-input type="number" v-model="assetForm.usefulLifeYears" class="form-control" />
               </b-col>
               <b-col md="6" class="mb-2">
                 <label class="font-weight-bold text-dark mb-50">Depreciation Method</label>
                 <v-select v-model="assetForm.depreciationMethod" :options="depreciationMethodOptions" />
               </b-col>

               <!-- Row 6: Location & Depreciation Rate -->
               <b-col md="6" class="mb-2">
                 <label class="font-weight-bold text-dark mb-50">Location / Dept</label>
                 <b-form-input v-model="assetForm.location" placeholder="e.g. IT Lab, Admin Office" class="form-control" />
               </b-col>
               <b-col md="6" class="mb-2">
                 <label class="font-weight-bold text-dark mb-50">Depreciation Rate (%)</label>
                 <b-form-input type="number" v-model="assetForm.depreciationRate" class="form-control" />
               </b-col>

                <!-- Opening Balance Field Removed -->

                <!-- Description Field Removed -->
             </b-row>
          </b-card>

          <div class="sidebar-footer sticky-footer d-flex gap-2">
            <b-button 
              type="submit" 
              variant="gradient-primary" 
              block 
              size="lg" 
              class="py-1 shadow-primary transition-all hover-translate-y"
              :disabled="request"
            >
              <b-spinner v-if="request" small class="mr-1" />
              <feather-icon v-else icon="SaveIcon" size="18" class="mr-50 h-5" />
            Save
            </b-button>
          </div>
        </b-form>
      </div>
    </b-sidebar>

    <!-- Fiscal Year Management Sidebar -->
    <b-sidebar
      id="fiscal-year-sidebar"
      v-model="isFiscalYearSidebarOpen"
      bg-variant="white"
      backdrop
      shadow
      width="450px"
      no-header
      right
      class="premium-sidebar"
    >
      <div class="sidebar-header-modern d-flex justify-content-between align-items-center p-2 border-bottom bg-light">
        <div class="d-flex align-items-center text-dark">
          <div class="sidebar-icon-box bg-white mr-1 shadow-sm">
            <feather-icon icon="CalendarIcon" size="20" class="text-primary" />
          </div>
          <h4 class="mb-0 font-weight-bolder text-dark">Fiscal Years</h4>
        </div>
        <b-button variant="flat-secondary" class="btn-icon rounded-circle border-0" @click="toggleFiscalYearSidebar(false)">
          <feather-icon icon="XIcon" size="22" />
        </b-button>
      </div>

      <div class="sidebar-body-modern p-2">
        <!-- Add Fiscal Year Form -->
        <b-card no-body class="p-2 mb-2 shadow-sm border-light">
          <h6 class="text-primary font-weight-bold mb-2">Register New Period</h6>
          <b-form @submit.prevent="saveFiscalYear">
            <b-row>
              <b-col md="12" class="mb-1">
                <label class="font-small-4 font-weight-bold">Period Title</label>
                <b-form-input v-model="fiscalYearForm.title" placeholder="e.g. 2024-2025" required class="shadow-none border-light" />
              </b-col>
              <b-col md="6" class="mb-1">
                <label class="font-small-4 font-weight-bold">Start Date</label>
                <flat-pickr 
                  v-model="fiscalYearForm.startDate" 
                  :config="datePickerConfig"
                  class="form-control bg-white shadow-none border-light" 
                  placeholder="Select start" 
                />
              </b-col>
              <b-col md="6" class="mb-1">
                <label class="font-small-4 font-weight-bold">End Date</label>
                <flat-pickr 
                  v-model="fiscalYearForm.endDate" 
                  :config="datePickerConfig"
                  class="form-control bg-white shadow-none border-light" 
                  placeholder="Select end" 
                />
              </b-col>
              <b-col md="12" class="mt-1">
                <b-button variant="gradient-primary" block type="submit" :disabled="savingFiscalYear">
                  <b-spinner v-if="savingFiscalYear" small class="mr-1" />
                  <feather-icon v-else icon="SaveIcon" size="16" class="mr-50" />
                  Save Fiscal Period
                </b-button>
              </b-col>
            </b-row>
          </b-form>
        </b-card>

        <!-- Fiscal Years List -->
        <div class="mt-3">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <h6 class="text-uppercase text-muted font-weight-bold mb-0 font-small-2">Previous Records</h6>
            <b-badge variant="light-primary" pill>{{ fiscalYears.length }} Periods</b-badge>
          </div>
          
          <div v-if="fiscalYears.length > 0" class="fiscal-years-list shadow-sm border rounded overflow-hidden">
            <b-table 
              :items="fiscalYears" 
              :fields="fiscalYearFields" 
              small 
              hover 
              responsive 
              class="mb-0 text-center"
              thead-class="bg-light"
            >
              <template #cell(startDate)="data">
                <span class="font-small-3">{{ new Date(data.value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) }}</span>
              </template>
              <template #cell(endDate)="data">
                <span class="font-small-3">{{ new Date(data.value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) }}</span>
              </template>
            </b-table>
          </div>
          <div v-else class="text-center py-4 bg-light rounded shadow-none border-dashed">
            <feather-icon icon="InboxIcon" size="32" class="text-muted mb-1 opacity-50" />
            <p class="text-muted mb-0 font-small-3 font-weight-bold">No active fiscal periods</p>
          </div>
        </div>
      </div>
    </b-sidebar>

    <!-- View Asset Sidebar -->
    <b-sidebar
      id="view-asset-sidebar"
      v-model="isViewSidebarOpen"
      bg-variant="white"
      backdrop
      shadow
      width="450px"
      no-header
      right
      class="premium-sidebar"
    >
      <div class="sidebar-header-modern d-flex justify-content-between align-items-center p-2 border-bottom bg-light">
        <div class="d-flex align-items-center text-dark">
          <div class="sidebar-icon-box bg-white mr-1 shadow-sm">
            <feather-icon icon="EyeIcon" size="20" class="text-primary" />
          </div>
          <h4 class="mb-0 font-weight-bolder text-dark">Asset Details</h4>
        </div>
        <b-button variant="flat-secondary" class="btn-icon rounded-circle border-0" @click="isViewSidebarOpen = false">
          <feather-icon icon="XIcon" size="22" />
        </b-button>
      </div>

      <div class="sidebar-body-modern p-0">
        <div v-if="viewingAsset" class="animate__animated animate__fadeIn">
          <!-- Quick Look Header -->
          <div class="bg-light p-2 mb-2 border-bottom">
            <div class="d-flex align-items-center mb-1">
              <div class="avatar bg-light-primary p-50 mr-1 rounded">
                <feather-icon icon="PackageIcon" size="24" class="text-primary" />
              </div>
              <div>
                <h5 class="mb-0 font-weight-bolder text-dark">{{ viewingAsset.name }}</h5>
                <b-badge variant="light-primary" pill class="font-small-2">{{ viewingAsset.categoryName || viewingAsset.category }}</b-badge>
              </div>
            </div>
            <div class="d-flex justify-content-between text-muted font-small-3">
              <span>Code: <strong>{{ viewingAsset.assetCode }}</strong></span>
              <span>Location: <strong>{{ viewingAsset.location || 'Not Set' }}</strong></span>
            </div>
          </div>

          <!-- Info Sections -->
          <div class="px-2 pb-2">
            <!-- Section: Primary Financials -->
            <div class="info-section mb-2">
              <h6 class="text-uppercase text-muted font-weight-bold mb-1 font-small-2 tracking-widest">Financial Summary</h6>
              <b-card no-body class="border-light shadow-none bg-light-secondary p-1">
                <b-row class="text-center">
                  <b-col cols="6" class="border-right-light">
                    <small class="text-muted d-block mb-25">Purchase Cost</small>
                    <h5 class="font-weight-bolder text-primary mb-0">{{ viewingAsset.purchaseCost }}</h5>
                  </b-col>
                  <b-col cols="6">
                    <small class="text-muted d-block mb-25">Salvage Value</small>
                    <h5 class="font-weight-bolder text-success mb-0">{{ viewingAsset.salvageValue }}</h5>
                  </b-col>
                </b-row>
              </b-card>
            </div>

            <!-- Section: Technical Details -->
            <div class="info-section mb-2">
              <h6 class="text-uppercase text-muted font-weight-bold mb-1 font-small-2 tracking-widest">Depreciation Metadata</h6>
              <div class="detail-row d-flex justify-content-between mb-1">
                <span class="text-secondary font-small-3">Useful Life</span>
                <span class="font-weight-bold font-small-3">{{ viewingAsset.usefulLifeYears }} Years</span>
              </div>
              <div class="detail-row d-flex justify-content-between mb-1">
                <span class="text-secondary font-small-3">Rate (%)</span>
                <span class="font-weight-bold font-small-3 text-warning">{{ viewingAsset.depreciationRate }}%</span>
              </div>
              <div class="detail-row d-flex justify-content-between mb-1">
                <span class="text-secondary font-small-3">Method</span>
                <span class="font-weight-bold font-small-3 text-info">{{ viewingAsset.depreciationMethod }}</span>
              </div>
            </div>

            <!-- Section: Timeline -->
            <div class="info-section mb-2">
              <h6 class="text-uppercase text-muted font-weight-bold mb-1 font-small-2 tracking-widest">Lifecycle Dates</h6>
              <div class="d-flex align-items-center mb-1">
                <div class="timeline-dot bg-primary mr-1"></div>
                <div>
                  <small class="text-muted d-block font-small-2">Purchase Date</small>
                  <span class="font-weight-bold font-small-3">{{ viewingAsset.purchaseDate | formatDate }}</span>
                </div>
              </div>
              <div class="d-flex align-items-center mb-1">
                <div class="timeline-dot bg-success mr-1"></div>
                <div>
                  <small class="text-muted d-block font-small-2">Capitalization Date</small>
                  <span class="font-weight-bold font-small-3">{{ viewingAsset.capitalizationDate | formatDate }}</span>
                </div>
              </div>
            </div>

            <!-- Section: Notes -->
            <div v-if="viewingAsset.description" class="info-section">
              <h6 class="text-uppercase text-muted font-weight-bold mb-1 font-small-2 tracking-widest">Description / Notes</h6>
              <p class="font-small-3 text-secondary p-1 bg-light rounded italic">
                {{ viewingAsset.description }}
              </p>
            </div>
          </div>
        </div>
        <div v-else class="text-center py-5">
          <div class="pulsing-circle mb-2 mx-auto">
             <feather-icon icon="InboxIcon" size="32" class="text-muted" />
          </div>
          <h5 class="text-secondary font-weight-bold">No Asset Selected</h5>
          <p class="text-muted px-3 font-small-3">Select an asset from the master list to view comprehensive details here.</p>
        </div>
      </div>
    </b-sidebar>

    <!-- Depreciation Management Modal -->
    <b-modal
      v-model="isDepreciationModalOpen"
      title="Depreciation Management"
      size="xl"
      hide-footer
      centered
      body-class="p-0 overflow-hidden"
      class="depreciation-modal-premium"
    >
      <div class="d-flex flex-column flex-md-row" style="min-height: 550px; max-height: 80vh;">
        <!-- Left Side: Selection & Periods -->
        <div class="depreciation-sidebar border-right bg-light-faint p-2" style="flex: 0 0 400px; overflow-y: auto;">
          <div class="section-title mb-2">
            <h6 class="text-uppercase text-muted font-weight-bold font-small-2 tracking-widest mb-1">Configuration</h6>
            <div v-if="selectedAssetForDepreciation" class="p-1 rounded bg-light-warning d-flex align-items-start mb-2">
              <feather-icon icon="InfoIcon" size="16" class="text-warning mr-50 mt-25" />
              <div>
                <small class="text-muted d-block">Target Asset</small>
                <span class="font-weight-bold font-small-3 text-dark">{{ selectedAssetForDepreciation.name }}</span>
              </div>
            </div>
            
            <label class="font-small-4 font-weight-bold text-dark mb-50">Select Fiscal Year</label>
            <v-select
              v-model="selectedDepreciationYear"
              :options="fiscalYears"
              :reduce="opt => opt.id"
              label="title"
              placeholder="Choose fiscal year..."
              @input="onFiscalYearChange"
              class="modern-select shadow-sm bg-white"
            />
          </div>

          <div v-if="selectedDepreciationYear" class="animate__animated animate__fadeIn mt-2">
            <div class="section-title mb-1">
               <h6 class="text-uppercase text-muted font-weight-bold font-small-2 tracking-widest mb-1 px-50">Operation Console</h6>
            </div>
            
            <div class="action-card-modern p-1 rounded-xl bg-white border shadow-sm mx-50">
                <b-button 
                  variant="gradient-success" 
                  block
                  size="lg"
                  class="action-btn-main shadow-success mb-1"
                  @click="runDepreciation" 
                  :disabled="runningDepreciation"
                >
                  <b-spinner v-if="runningDepreciation" small class="mr-50" />
                  <feather-icon v-else icon="CpuIcon" size="18" class="mr-50" />
                  Process
                </b-button>
                
                <b-button 
                  variant="flat-primary" 
                  block
                  size="lg"
                  @click="loadDepreciationSchedule"
                  :disabled="loadingSchedule"
                  class="action-btn-secondary"
                >
                  <b-spinner v-if="loadingSchedule" small class="mr-50" />
                  <feather-icon v-else icon="FileTextIcon" size="18" class="mr-50" />
                  History
                </b-button>
            </div>
          </div>
          
          <div v-else class="text-center py-5 opacity-50">
            <feather-icon icon="SettingsIcon" size="48" class="text-muted mb-2" />
            <p class="text-muted px-2">Select a fiscal year to begin processing depreciation</p>
          </div>
        </div>

        <!-- Right Side: Details & History -->
        <div class="depreciation-content flex-grow-1 p-2 bg-white" style="overflow-y: auto;">
          <div v-if="loadingSchedule" class="text-center py-5">
            <b-spinner variant="primary" small class="mb-1" />
            <p class="text-muted">Fetching depreciation records...</p>
          </div>

          <div v-else-if="depreciationSchedule.length > 0" class="animate__animated animate__fadeIn">
            <div class="d-flex justify-content-between align-items-end mb-2">
              <div>
                <h5 class="font-weight-bolder text-dark mb-0">Depreciation Schedule</h5>
                <small class="text-muted">Comprehensive history of asset valuation adjustments</small>
              </div>
              <b-button variant="outline-primary" size="sm" @click="loadDepreciationSchedule()">
                <feather-icon icon="RefreshCwIcon" size="14" class="mr-50" />
                Refresh
              </b-button>
            </div>

            <div class="schedule-table-card border rounded overflow-hidden shadow-sm">
              <b-table
                :items="normalizedDepreciationSchedule"
                :fields="depreciationFields"
                hover
                responsive
                striped
                class="mb-0 modern-table"
                thead-class="bg-light text-uppercase font-small-2"
                show-empty
              >
                <template #cell(period)="data">
                  <b-badge variant="light-secondary" pill>{{ data.value }}</b-badge>
                </template>

                <template #cell(asset)="data">
                  <div class="title-cell d-flex align-items-center">
                    <div class="avatar bg-light-info p-25 mr-50 rounded">
                      <feather-icon icon="BoxIcon" size="12" class="text-info" />
                    </div>
                    <span class="font-weight-bold text-dark font-small-3">{{ data.value }}</span>
                  </div>
                </template>

                <template #cell(openingValue)="data">
                  <span class="text-dark font-weight-bold">{{ data.value | formatCurrency }}</span>
                </template>

                <template #cell(depreciation)="data">
                  <span class="text-warning font-weight-bold">{{ data.value | formatCurrency }}</span>
                </template>

                <template #cell(accumulated)="data">
                  <span class="text-danger font-weight-bold">{{ data.value | formatCurrency }}</span>
                </template>

                <template #cell(closingValue)="data">
                  <div class="p-25 px-50 rounded bg-light-success d-inline-block">
                    <span class="text-success font-weight-bolder">{{ data.value | formatCurrency }}</span>
                  </div>
                </template>

                <template #empty>
                  <div class="text-center py-4">
                    <p class="text-muted mb-0">No records exist for this period.</p>
                  </div>
                </template>
              </b-table>
            </div>
            
            <div class="mt-2 p-1 bg-light rounded d-flex justify-content-between align-items-center">
               <div>
                  <small class="text-muted d-block">TOTAL RECORDS</small>
                  <span class="font-weight-bolder text-primary h5 mb-0">{{ normalizedDepreciationSchedule.length }}</span>
               </div>
               <div class="text-right">
                  <small class="text-muted d-block">LAST SYNC</small>
                  <span class="font-weight-bold text-dark font-small-2">{{ new Date().toLocaleTimeString() }}</span>
               </div>
            </div>
          </div>

          <div v-else class="text-center py-5 h-100 d-flex flex-column align-items-center justify-content-center opacity-75">
            <div class="empty-schedule-icon mb-2">
              <feather-icon icon="BarChart2Icon" size="64" class="text-muted opacity-25" />
            </div>
            <h5 class="text-secondary font-weight-bold">No Data Visualized</h5>
            <p class="text-muted px-5 max-w-400">
              Select a fiscal year from the left menu and click <strong>Run</strong> 
              to calculate depreciation or <strong>View</strong> to see existing records.
            </p>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Add/Edit Category Modal -->
    <b-modal
      v-model="isCategoryModalOpen"
      :title="editingCategoryId ? 'Edit Category' : 'Add Category'"
      centered
      hide-footer
      body-class="p-2"
    >
      <b-form @submit.prevent="saveCategoryEdit">
        <b-form-group label="Category Name" label-for="category-name">
          <b-form-input
            id="category-name"
            v-model="editingCategoryName"
            placeholder="Enter category name"
            required
            class="form-control-lg"
          />
        </b-form-group>
        
        <b-row class="mt-2">
          <b-col cols="6">
            <b-button
              type="submit"
              variant="gradient-primary"
              block
              :disabled="savingCategory"
            >
              <b-spinner v-if="savingCategory" small class="mr-1" />
              <feather-icon v-else icon="SaveIcon" size="16" class="mr-50" />
              Save
            </b-button>
          </b-col>
          <b-col cols="6">
          <b-button
            variant="outline-secondary"
            block
            @click="closeCategoryModal"
          >
            Cancel
          </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BButton, BTable, BPagination, 
  BSpinner, BSidebar, BForm, BBadge, VBTooltip, BModal, BInputGroup, BInputGroupAppend, BFormGroup
} from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import { mapActions } from "vuex";

export default {
  name: "FixedAssets",
  components: {
    BCard, BRow, BCol, BFormInput, BButton, BTable, BPagination,
    BSpinner, BSidebar, BForm, BBadge, vSelect, flatPickr, BModal, BInputGroup, BInputGroupAppend, BFormGroup
  },
  directives: { 
    Ripple, 
    'b-tooltip': VBTooltip 
  },
  data() {
    return {
      rights: { add: true, edit: true, delete: true },
      searchQuery: "",
      pageSize: 10,
      currentPage: 1,
      totalRecords: 0,
      dataLoading: false,
      isSidebarOpen: false,
      isSettingsSidebarOpen: false,
      isViewSidebarOpen: false,
      viewingAsset: null,
      sidebarMode: "add",
      request: false,
      generatingCode: false,
      savingSetting: false,
      isFiscalYearSidebarOpen: false,
      savingFiscalYear: false,
      fiscalYears: [],
      fiscalYearForm: {
        id: 0,
        campusID: this.$store.state.userData.cId,
        title: "",
        startDate: null,
        endDate: null,
      },
      fiscalYearFields: [
        { key: 'title', label: 'Fiscal Year', tdClass: 'font-weight-bold' },
        { key: 'startDate', label: 'Start' },
        { key: 'endDate', label: 'End' },
      ],

      // Depreciation Management
      isDepreciationModalOpen: false,
      selectedDepreciationYear: null,
      selectedAssetForDepreciation: null,
      runningDepreciation: false,
      loadingSchedule: false,
      depreciationSchedule: [],
      depreciationFields: [
        { key: 'period', label: 'Period' },
        { key: 'asset', label: 'Asset', sortable: true },
        { key: 'openingValue', label: 'Opening', thClass: 'text-right', tdClass: 'text-right' },
        { key: 'depreciation', label: 'Depreciation', thClass: 'text-right', tdClass: 'text-right' },
        { key: 'accumulated', label: 'Accumulated', thClass: 'text-right', tdClass: 'text-right' },
        { key: 'closingValue', label: 'Closing', thClass: 'text-right', tdClass: 'text-right' },
      ],
      
      assets: [],
      accList: [],
      parentOptions: [],
      selectedAccountID: null,
      
      assetForm: {
        id: 0,
        campusID: this.$store.state.userData.cId,
        assetCode: "",
        name: "",
        category: "",
        coaID: 0,
        code: "",
        parentHead: 0,
        nature: "Asset",
        description: "",
        purchaseDate: null,
        capitalizationDate: null,
        purchaseCost: 0,
        salvageValue: 0,
        usefulLifeYears: 0,
        depreciationMethod: "Straight Line (SL)",
        location: "",
        depreciationRate: 0,
        openingBalance: 0,
        additions: 0,
        deletions: 0,
        accumulatedDepreciation: 0,
      },

      fields: [
        { key: "sno", label: "#", thClass: "text-center", tdClass: "text-center" },
        { key: "name", label: "Asset Identity", sortable: true },
        { key: "categoryName", label: "Category", thClass: "text-center", tdClass: "text-center" },
        { key: "purchaseDate", label: "Purchase Date", thClass: "text-center", tdClass: "text-center" },
        { key: "purchaseCost", label: "Cost", thClass: "text-right", tdClass: "text-right font-weight-bold text-primary" },
        { key: "usefulLifeYears", label: "Life", thClass: "text-center", tdClass: "text-center" },
        { key: "location", label: "Location", thClass: "text-center", tdClass: "text-center" },
        { key: "actions", label: "Actions", thClass: "text-right", tdClass: "text-right" },
      ],

      modalPeriodFields: [
        { key: "title", label: "Period" },
        { key: "startDate", label: "Start Date" },
        { key: "endDate", label: "End Date" },
        { key: "actions", label: "Actions", tdClass: "text-right" },
      ],

      natureOptions: [{ name: "Asset" }, { name: "Liability" }, { name: "Equity" }, { name: "Revenue" }, { name: "Expense" }],
      categoryOptions: [],
      depreciationMethodOptions: ["Straight Line (SL)", "Reducing Balance"],
      fixedAssetSettingValue: 0,
      
      // Category Edit Modal
      isCategoryModalOpen: false,
      editingCategoryName: "",
      editingCategoryId: null,
      savingCategory: false,
      
      // FlatPickr configuration
      datePickerConfig: {
        altInput: true,
        altFormat: 'j M, Y',
        dateFormat: 'Y-m-d',
        allowInput: true
      }
    };
  },
  filters: {
    formatDate(value) {
      if (!value) return 'N/A';
      return new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    },
    formatCurrency(value) {
      if (typeof value !== 'number') {
        const parsedValue = parseFloat(value);
        if (isNaN(parsedValue)) {
          return '0.00';
        }
        value = parsedValue;
      }
      return value.toFixed(2);
    }
  },
  computed: {
    sidebarTitle() { return this.sidebarMode === "add" ? "Register Fixed Asset" : "Update Asset Record"; },
    displayedRange() {
      const start = (this.currentPage - 1) * this.pageSize + 1;
      const end = Math.min(this.currentPage * this.pageSize, this.totalRecords);
      return { start, end };
    },
    normalizedDepreciationSchedule() {
      const normalized = [];
      
      this.depreciationSchedule.forEach(item => {
        // Check if it's already flattened data from LoadDepreciationSchedule API
        if (item.period && item.asset && item.openingValue !== undefined) {
          // Already flat format from History API
          normalized.push(item);
        } 
        // Check if it's nested data from RunDepreciation API
        else if (item.logs && item.logs.length > 0) {
          // Nested format from Process API - flatten it
          item.logs.forEach(log => {
            normalized.push({
              period: item.title,
              asset: log.assetName,
              openingValue: log.openingBalance,
              depreciation: log.depreciation,
              accumulated: log.accumulated,
              closingValue: log.closingBalance
            });
          });
        }
      });
      
      return normalized;
    }
  },
  watch: {
    isSidebarOpen(newVal) {
      if (newVal && this.sidebarMode === "add") {
        this.generateCode();
      }
    }
  },
  async created() {
    const routeRights = this.$store.state.rights[this.$route.name];
    if (routeRights) this.rights = routeRights;
    this.refreshData();
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),
    
    async refreshData() {
      this.dataLoading = true;
      await Promise.all([this.loadInitialData(), this.loadParents(), this.fetchFixedAssetSettings(), this.loadFiscalYears(), this.loadCategories()]);
      this.dataLoading = false;
    },

    async loadInitialData() {
      this.dataLoading = true;
      try {
        const query = this.searchQuery ? `&searchQuery=${this.searchQuery}` : '';
        const res = await this.get({ 
          url: `${this.$store.state.domain}FixedAsset/LoadAssets?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}${query}`, 
          token: this.$store.state.userData.token 
        });
        
        if (res) {
          this.assets = Array.isArray(res) ? res : (res.data ? res.data : []);
          this.totalRecords = res.totalCount || this.assets.length;
          this.accList = [...this.assets];
        }
      } finally {
        this.dataLoading = false;
      }
    },

    viewAsset(item) {
      this.viewingAsset = item;
      this.isViewSidebarOpen = true;
    },

    async loadParents() {
      const res = await this.get({ 
        url: this.$store.state.domain + "Accounts/LoadData?db=" + this.$store.state.userData.db + "&cID=" + this.$store.state.userData.cId, 
        token: this.$store.state.userData.token 
      });
      
      if (res) {
        if (res.status === "success" && res.data) this.parentOptions = res.data;
        else if (Array.isArray(res)) this.parentOptions = res;
        else if (res.data && Array.isArray(res.data)) this.parentOptions = res.data;
        else this.parentOptions = [];
      } else {
        this.parentOptions = [];
      }
    },

    async loadCategories() {
      try {
        const res = await this.get({
          url: `${this.$store.state.domain}AssetCategory?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}`,
          token: this.$store.state.userData.token
        });

        if (res) {
          let categories = [];
          
          // Handle different response structures
          if (res.status === "success" && res.data && Array.isArray(res.data)) {
            categories = res.data;
          } else if (Array.isArray(res)) {
            categories = res;
          } else if (res.data && Array.isArray(res.data)) {
            categories = res.data;
          }

          // Extract category objects from the response
          this.categoryOptions = categories.map(cat => ({
            id: cat.id || cat.ID || 0,
            name: cat.name || cat.categoryName || "Unknown"
          }));
        } else {
          this.categoryOptions = [];
        }
      } catch (error) {
        console.error("Failed to load categories:", error);
        this.categoryOptions = [];
      }
    },


    async generateCode() {
      // Always fetch settings first to ensure we have the latest value
      await this.fetchFixedAssetSettings();
      
      console.log("Generating code with parentID:", this.fixedAssetSettingValue);
      
      if (!this.fixedAssetSettingValue || this.fixedAssetSettingValue === 0) {
        this.$bvToast.toast("Please configure the Fixed Asset parent account in settings first.", {
          title: "Configuration Required",
          variant: "warning",
          solid: true
        });
        return;
      }
      
      this.generatingCode = true;
      try {
        // Use both parentID and parentid for safety/compatibility
        const url = `${this.$store.state.domain}Accounts/GenerateCode?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}&parentID=${this.fixedAssetSettingValue}&parentid=${this.fixedAssetSettingValue}`;
        
        console.log("Calling GenerateCode API:", url);
        
        const res = await this.get({ url, token: this.$store.state.userData.token });
        
        console.log("GenerateCode API response:", res);
        
        if (res) {
          const generatedCode = (res.data !== undefined && res.data !== null) ? res.data : (res.code || res);
          this.assetForm.assetCode = generatedCode.toString().replace(/[\[\]]/g, '').trim();
          this.assetForm.code = this.assetForm.assetCode;
          // Crucial: Set parentHead in the form too
          this.assetForm.parentHead = this.fixedAssetSettingValue;
          
          console.log("Generated code:", this.assetForm.assetCode, "with parentHead:", this.assetForm.parentHead);
          
          this.$bvToast.toast(`Asset code generated: ${this.assetForm.assetCode}`, {
            title: this.$t("Success"),
            variant: "success",
            solid: true,
            autoHideDelay: 2000
          });
        }
      } catch (error) {
        console.error("Error generating code:", error);
        this.$bvToast.toast("Failed to generate asset code. Please try again.", {
          title: this.$t("Error"),
          variant: "danger",
          solid: true
        });
      } finally {
        this.generatingCode = false;
      }
    },

    async handleSaveAsset() {
      this.request = true;
      try {
        // Ensure parentHead is set from settings if not already present in form
        const finalParentHead = Number(this.assetForm.parentHead) || Number(this.fixedAssetSettingValue) || 0;
        
        const payload = {
          ...this.assetForm,
          id: Number(this.assetForm.id) || 0,
          campusID: Number(this.$store.state.userData.cId) || 0,
          coaID: Number(this.assetForm.coaID) || 0,
          categoryID: Number(this.assetForm.categoryID) || 0,
          parentHead: finalParentHead,

          purchaseCost: Number(this.assetForm.purchaseCost) || 0,
          salvageValue: Number(this.assetForm.salvageValue) || 0,
          usefulLifeYears: Number(this.assetForm.usefulLifeYears) || 0,
          depreciationRate: Number(this.assetForm.depreciationRate) || 0,
          additions: Number(this.assetForm.additions) || 0,
          deletions: Number(this.assetForm.deletions) || 0,
          accumulatedDepreciation: Number(this.assetForm.accumulatedDepreciation) || 0,
          // Forced values as requested
          openingBalance: 0,
          description: this.assetForm.description || "",
          db: this.$store.state.userData.db
        };
        
        console.log("Saving asset with payload:", payload);
        
        const res = await this.post({
          url: this.$store.state.domain + "FixedAsset/Save",
          token: this.$store.state.userData.token,
          body: payload,
          message: "Asset record " + (this.sidebarMode === "edit" ? "updated" : "created") + " successfully.",
          context: this
        });
        
        if (res) {
          this.toggleSidebar(false);
          this.refreshData();
        }
      } finally {
        this.request = false;
      }
    },

    async saveAccountSetting() {
      this.savingSetting = true;
      try {
        const res = await this.post({
          url: this.$store.state.domain + "Settings/SaveFixedAssetKey?db=" + this.$store.state.userData.db + "&cID=" + this.$store.state.userData.cId + "&assetID=" + this.selectedAccountID,
          token: this.$store.state.userData.token,
          body: {},
          message: "Asset mapping key updated successfully.",
          context: this
        });
        if (res) {
          this.toggleSettingsSidebar(false);
          this.refreshData();
        }
      } finally {
        this.savingSetting = false;
      }
    },

    openAddAssetSidebar() {
      this.sidebarMode = "add";
      this.resetAssetForm();
      this.isSidebarOpen = true;
    },

    async fetchFixedAssetSettings() {
      try {
        const res = await this.post({
          url: `${this.$store.state.domain}Settings/getSettingsAll?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}`,
          body: ["fixed_assest_key"],
          token: this.$store.state.userData.token,
          returnData: true,
          subjects: true
        });
        
        console.log("fetchFixedAssetSettings response:", res);
        
        if (res) {
          // Handle response structure: {status: "success", data: [...]} OR just data array if flags set it
          let settings = [];
          if (res.status === "success" && res.data && Array.isArray(res.data)) {
            settings = res.data;
          } else if (Array.isArray(res)) {
            settings = res;
          } else if (res.data && Array.isArray(res.data)) {
            settings = res.data;
          } else if (res.settings && Array.isArray(res.settings)) {
            settings = res.settings;
          }
          
          // Use 'find' with trim() and case-insensitive check just in case
          const setting = settings.find(s => 
            s.key && (s.key.trim().toLowerCase() === "fixed_assest_key" || s.key.trim().toLowerCase() === "fixed_asset_key")
          );

          if (setting) {
            // Priority: valueNumber > value
            const val = (setting.valueNumber !== null && setting.valueNumber !== undefined) 
              ? setting.valueNumber 
              : setting.value;
            
            this.fixedAssetSettingValue = parseInt(val) || 0;
            this.assetForm.parentHead = this.fixedAssetSettingValue;
            console.log("Fixed Asset Setting Value parsed:", this.fixedAssetSettingValue);
          } else {
            console.warn("Fixed asset setting NOT found in settings list:", settings);
          }
        }
      } catch (e) {
        console.error("Failed to fetch fixed asset settings", e);
      }
    },

    async editAsset(item) {
      this.dataLoading = true;
      try {
        // Refresh settings to be absolutely sure we have the latest parent ID
        if (!this.fixedAssetSettingValue || this.fixedAssetSettingValue === 0) {
          await this.fetchFixedAssetSettings();
        }

        const res = await this.get({
          url: `${this.$store.state.domain}FixedAsset/LoadSelected?db=${this.$store.state.userData.db}&id=${item.id}`,
          token: this.$store.state.userData.token
        });
        
        if (res) {
          // Use the setting value as priority for parentHead
          const finalParentHead = this.fixedAssetSettingValue || res.parentHead || 0;
          
          // Reset form first to ensure clean state
          this.resetAssetForm();
          
          // Populate with loaded data
          this.assetForm = {
            id: res.id || item.id,
            campusID: res.campusID || this.$store.state.userData.cId,
            assetCode: res.assetCode || res.code || "",
            name: res.name || "",
            category: res.category || res.categoryName || "",
            categoryID: res.categoryID || null,
            coaID: res.coaID || 0,
            code: res.code || res.assetCode || "",
            parentHead: finalParentHead,
            nature: res.nature || "Asset",
            description: res.description || "",
            purchaseDate: res.purchaseDate || null,
            capitalizationDate: res.capitalizationDate || null,
            purchaseCost: res.purchaseCost || null,
            salvageValue: res.salvageValue || null,
            usefulLifeYears: res.usefulLifeYears || null,
            depreciationMethod: res.depreciationMethod || "Straight Line (SL)",
            location: res.location || "",
            depreciationRate: res.depreciationRate || null,
            openingBalance: res.openingBalance || 0,
            additions: res.additions || 0,
            deletions: res.deletions || 0,
            accumulatedDepreciation: res.accumulatedDepreciation || 0,
          };
          
          // Ensure categoryID is synced if category name exists but categoryID is missing
          if (!this.assetForm.categoryID && this.assetForm.category) {
            const cat = this.categoryOptions.find(c => c.name === this.assetForm.category);
            if (cat) this.assetForm.categoryID = cat.id;
          }
          
          this.sidebarMode = "edit";
          this.isSidebarOpen = true;
        } else {
          // Fallback to local item if LoadSelected fails
          const finalParentHead = this.fixedAssetSettingValue || item.parentHead || 0;
          
          // Reset form first
          this.resetAssetForm();
          
          // Populate with item data
          this.assetForm = {
            id: item.id,
            campusID: item.campusID || this.$store.state.userData.cId,
            assetCode: item.assetCode || item.code || "",
            name: item.name || "",
            category: item.category || item.categoryName || "",
            categoryID: item.categoryID || null,
            coaID: item.coaID || 0,
            code: item.code || item.assetCode || "",
            parentHead: finalParentHead,
            nature: item.nature || "Asset",
            description: item.description || "",
            purchaseDate: item.purchaseDate || null,
            capitalizationDate: item.capitalizationDate || null,
            purchaseCost: item.purchaseCost || null,
            salvageValue: item.salvageValue || null,
            usefulLifeYears: item.usefulLifeYears || null,
            depreciationMethod: item.depreciationMethod || "Straight Line (SL)",
            location: item.location || "",
            depreciationRate: item.depreciationRate || null,
            openingBalance: item.openingBalance || 0,
            additions: item.additions || 0,
            deletions: item.deletions || 0,
            accumulatedDepreciation: item.accumulatedDepreciation || 0,
          };
          
          if (!this.assetForm.categoryID && this.assetForm.category) {
            const cat = this.categoryOptions.find(c => c.name === this.assetForm.category);
            if (cat) this.assetForm.categoryID = cat.id;
          }

          this.sidebarMode = "edit";
          this.isSidebarOpen = true;
        }
      } finally {
        this.dataLoading = false;
      }
    },

    resetAssetForm() {
      this.assetForm = {
        id: 0,
        campusID: this.$store.state.userData.cId,
        assetCode: "",
        name: "",
        category: "",
        categoryID: null,
        coaID: 0,
        code: "",
        parentHead: this.fixedAssetSettingValue || 0,
        nature: "Asset",
        description: "",
        purchaseDate: null,
        capitalizationDate: null,
        purchaseCost: null,
        salvageValue: null,
        usefulLifeYears: null,
        depreciationMethod: "Straight Line (SL)",
        location: "",
        depreciationRate: null,
        openingBalance: 0,
        additions: 0,
        deletions: 0,
        accumulatedDepreciation: 0,
      };
    },

    toggleSidebar(val) { 
      this.isSidebarOpen = val;
      // Reset form when sidebar closes
      if (!val) {
        this.resetAssetForm();
        this.sidebarMode = "add";
      }
    },
    async toggleSettingsSidebar(val) { 
      this.isSettingsSidebarOpen = val; 
      if (val && this.parentOptions.length === 0) {
        await this.loadParents();
      }
    },
    onSearchChange() { this.loadInitialData(); },

    // Depreciation Management Methods
    openDepreciationModal(asset = null) {
      this.isDepreciationModalOpen = true;
      this.depreciationSchedule = [];
      this.selectedDepreciationYear = null;
      this.selectedAssetForDepreciation = asset;
    },

    async onFiscalYearChange(yearID) {
      if (!yearID) {
        this.depreciationSchedule = [];
        return;
      }
      // Auto-load removed as requested
      this.depreciationSchedule = [];
    },

    async runDepreciation() {
      if (!this.selectedDepreciationYear) {
        this.$bvToast.toast("Please select a fiscal year", { variant: "warning" });
        return;
      }

      this.runningDepreciation = true;
      try {
        // Build the request body - pass asset ID array if individual asset is selected
        let body = null;
        if (this.selectedAssetForDepreciation && this.selectedAssetForDepreciation.id) {
          body = [this.selectedAssetForDepreciation.id];
        }
        
        const res = await this.post({
          url: `${this.$store.state.domain}FixedAsset/RunDepreciation?db=${this.$store.state.userData.db}&campusID=${this.$store.state.userData.cId}&fiscalYearID=${this.selectedDepreciationYear}`,
          token: this.$store.state.userData.token,
          body: body,
          context: this,
          allResponse: true  // Full response (data + skippedAssets); no store toast
        });
        
        if (res) {
          let responseData = null;
          if (res.status === 'success' && res.data) {
            responseData = res.data;
          } else if (res.data && res.data.data) {
            responseData = res.data.data;
          } else if (Array.isArray(res.data)) {
            responseData = res.data;
          } else if (Array.isArray(res)) {
            responseData = res;
          }
          
                                if (Array.isArray(responseData) && responseData.length > 0) {
          
                                  this.depreciationSchedule = responseData;
          
                                } else {
          
                                  this.depreciationSchedule = [];
          
                                }
          
                      
          
                                let hasSkippedAssets = false;
                                const hasAnyLogs = Array.isArray(responseData) && responseData.some(p => p.logs && p.logs.length > 0);

                                // --- Skipped assets: show first reason directly when no logs (or as single toast) ---
                                if (res.skippedAssets && Array.isArray(res.skippedAssets) && res.skippedAssets.length > 0) {
                                  hasSkippedAssets = true;
                                  const first = res.skippedAssets[0];
                                  const reason = first.reason || "Asset was skipped.";
                                  const msg = !hasAnyLogs
                                    ? reason
                                    : `Asset "${first.assetName}" was skipped. Reason: ${reason}`;
                                  this.$bvToast.toast(msg, {
                                    title: "Depreciation Skipped",
                                    variant: "warning",
                                    solid: true,
                                    autoHideDelay: 7000,
                                    toaster: "b-toaster-top-center",
                                  });
                                }
          
                      
          
                                // Add a generic success message only if no assets were skipped
          
                                if (!hasSkippedAssets) {
          
                                  this.$bvToast.toast(
          
                                    this.selectedAssetForDepreciation
          
                                      ? `Depreciation calculated for ${this.selectedAssetForDepreciation.name} successfully.`
          
                                      : "Depreciation calculated successfully.",
          
                                    {
          
                                      title: this.$t("Success"),
          
                                      variant: "success",
          
                                      solid: true,
          
                                      autoHideDelay: 3000,
          
                                    }
          
                                  );
          
                                }
          
                              }      } catch (error) {
        this.$bvToast.toast("Failed to run depreciation", { variant: "danger" });
      } finally {
        this.runningDepreciation = false;
      }
    },

    async loadDepreciationSchedule() {
      this.loadingSchedule = true;
      try {
        // Build URL with optional assetID parameter for individual asset filtering
        let url = `${this.$store.state.domain}FixedAsset/LoadDepreciationSchedule?db=${this.$store.state.userData.db}&campusID=${this.$store.state.userData.cId}&fiscalYearID=0`;
        
        if (this.selectedAssetForDepreciation && this.selectedAssetForDepreciation.id) {
          url += `&assetID=${this.selectedAssetForDepreciation.id}`;
        }
        
        const res = await this.get({
          url: url,
          token: this.$store.state.userData.token
        });

        if (res) {
          this.depreciationSchedule = Array.isArray(res) ? res : (res.data ? res.data : []);
        }
      } catch (error) {
        this.$bvToast.toast("Failed to load depreciation history", { variant: "danger" });
      } finally {
        this.loadingSchedule = false;
      }
    },

    async loadDepreciationHistory() {
        await this.loadDepreciationSchedule();
    },

    getNatureVariant(nature) {
      const variants = { 'Asset': 'light-primary', 'Liability': 'light-danger', 'Equity': 'light-success', 'Revenue': 'light-warning', 'Expense': 'light-info' };
      return variants[nature] || 'light-secondary';
    },

    async confirmDelete(item) {
      const result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: `Permanently delete asset: ${item.name}?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#ea5455",
        cancelButtonColor: "#82868b",
        confirmButtonText: "Yes, delete it!",
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      });

      if (result.isConfirmed) {
        const res = await this.delete({
          url: `${this.$store.state.domain}FixedAsset/Delete?db=${this.$store.state.userData.db}&id=${item.id}`,
          token: this.$store.state.userData.token,
          message: "Asset deleted successfully",
          context: this
        });
        if (res) {
          await this.refreshData();
        }
      }
    },

    async loadFiscalYears() {
      try {
        const res = await this.get({
          url: `${this.$store.state.domain}FiscalYear/LoadFiscalYears?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}`,
          token: this.$store.state.userData.token
        });
        if (res) {
          // Handle both array direclty or wrapped in data object
          this.fiscalYears = Array.isArray(res) ? res : (res.data && Array.isArray(res.data) ? res.data : []);
        }
      } catch (error) {
        console.error("Failed to load fiscal years", error);
      }
    },

    async saveFiscalYear() {
      this.savingFiscalYear = true;
      let success = false;
      try {
        const payload = {
          ...this.fiscalYearForm,
          db: this.$store.state.userData.db
        };
        
        const res = await this.post({
          url: `${this.$store.state.domain}FiscalYear/Save`,
          token: this.$store.state.userData.token,
          body: payload,
          message: "Fiscal Year saved successfully.",
          context: this
        });

        // Check if response indicates success
        if (res) {
          success = true;
          await this.loadFiscalYears();
          this.resetFiscalYearForm();
        }
      } catch (error) {
        // Only show error toast if we haven't marked it as success
        // This prevents post-success logic failures from showing "Failed to save"
        if (!success) {
          const msg = error.response?.data?.message || error.message || 'Unknown error';
          this.$bvToast.toast(`Failed to save fiscal year: ${msg}`, {
            title: this.$t("Error"),
            variant: "danger",
            solid: true
          });
        }
      } finally {
        this.savingFiscalYear = false;
      }
    },

    resetFiscalYearForm() {
      this.fiscalYearForm = {
        id: 0, campusID: this.$store.state.userData.cId, title: "", startDate: null, endDate: null
      };
    },

    toggleFiscalYearSidebar(val) {
      this.isFiscalYearSidebarOpen = val;
      if (val) this.loadFiscalYears();
    },

    // Category Edit Methods
    openEditCategoryModal() {
      // If a category is selected, get its name for editing
      if (this.assetForm.categoryID) {
        const selectedCategory = this.categoryOptions.find(c => c.id === this.assetForm.categoryID);
        if (selectedCategory) {
          this.editingCategoryName = selectedCategory.name || "";
          this.editingCategoryId = selectedCategory.id;
        } else {
          // Fallback to category name if ID not found
          this.editingCategoryName = this.assetForm.category || "";
          this.editingCategoryId = this.assetForm.categoryID;
        }
      } else {
        // No category selected - add new mode
        this.editingCategoryName = "";
        this.editingCategoryId = null;
      }
      this.isCategoryModalOpen = true;
    },

    async saveCategoryEdit() {
      if (!this.editingCategoryName || !this.editingCategoryName.trim()) {
        this.$bvToast.toast("Please enter a category name", {
          title: "Validation Error",
          variant: "warning",
          solid: true
        });
        return;
      }

      this.savingCategory = true;
      try {
        const payload = {
          id: this.editingCategoryId || 0, // Use existing ID if editing, 0 for new
          campusID: this.$store.state.userData.cId,
          name: this.editingCategoryName.trim()
        };

        const res = await this.post({
          url: `${this.$store.state.domain}AssetCategory?db=${this.$store.state.userData.db}`,
          token: this.$store.state.userData.token,
          body: payload,
          message: this.editingCategoryId ? "Category updated successfully" : "Category saved successfully",
          context: this
        });

        if (res) {
          // Reload categories from API to get the updated list
          await this.loadCategories();
          
          // Update the category in the form - find by name (in case ID changed)
          const updatedCat = this.categoryOptions.find(c => c.name === this.editingCategoryName.trim());
          if (updatedCat) {
            this.assetForm.categoryID = updatedCat.id;
            this.assetForm.category = updatedCat.name;
          } else {
            // Fallback: just update the name
            this.assetForm.category = this.editingCategoryName.trim();
          }
          
          this.isCategoryModalOpen = false;
          this.editingCategoryName = "";
          this.editingCategoryId = null;
        }
      } catch (error) {
        console.error("Error saving category:", error);
        this.$bvToast.toast("Failed to save category. Please try again.", {
          title: this.$t("Error"),
          variant: "danger",
          solid: true
        });
      } finally {
        this.savingCategory = false;
      }
    },

    closeCategoryModal() {
      this.isCategoryModalOpen = false;
      this.editingCategoryName = "";
      this.editingCategoryId = null;
    }
  }
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
@import url('https://cdnjs.cloudflare.com/ajax/libs/animate.css/4.1.1/animate.min.css');

.fixed-assets-management {
  .resources-list-card {
    border-radius: 12px;
    position: relative;
    overflow: hidden;
  }

  .gap-2 {
    gap: 0.5rem;
  }

  .gap-50 {
    gap: 0.25rem;
  }

  .flex-grow-1 {
    flex-grow: 1;
  }

  .py-50 {
    padding-top: 0.375rem !important;
    padding-bottom: 0.375rem !important;
  }

  .gradient-line-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #7367f0 0%, #9d7aff 50%, #cbb9ff 100%);
  }

  .resource-title {
    font-size: 1.25rem;
    font-weight: 800;
    color: #2d1b69;
    letter-spacing: -0.3px;
  }

  .filter-control {
    border-radius: 10px;
    border: 1.5px solid #e0d8ff;
    padding: 0.625rem 0.875rem;
    height: auto;
    transition: all 0.2s ease;
    
    &:focus {
      border-color: #7367f0;
      box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.1) !important;
    }
  }

  .pagination-controls-top {
    padding: 0.75rem 1.25rem;
    background: linear-gradient(180deg, #faf9ff 0%, #ffffff 100%);
    border: 1px solid #e0d8ff;
    border-radius: 10px;
    margin: 0 0.5rem 1rem 0.5rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
  }

  .page-size-wrapper {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    .page-size-label {
      font-size: 0.8125rem;
      font-weight: 600;
      color: #64748b;
      margin: 0;
      white-space: nowrap;
    }
  }

  .page-size-select-compact {
    min-width: 85px;
    width: 85px;
    
    ::v-deep .vs__dropdown-toggle {
      height: 38px !important;
      min-height: 38px !important;
      border-radius: 8px !important;
      border: 1.5px solid #e2e8f0 !important;
      padding: 0 0.5rem !important;
      background: #fff !important;
      font-size: 0.8125rem !important;
      display: flex;
      align-items: center;
      
      &:hover {
        border-color: #7367f0 !important;
      }
    }
    
    ::v-deep .vs__selected {
      color: #334155 !important;
      font-weight: 600 !important;
      font-size: 0.875rem !important;
      margin: 0 !important;
      padding: 0 !important;
      position: relative !important;
      top: 1px;
    }

    ::v-deep .vs__actions {
      padding: 0 !important;
      margin-top: -2px;
    }

    ::v-deep .vs__search {
      margin: 0 !important;
      padding: 0 !important;
    }
  }

  .pagination-count-display {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    
    .count-text {
      font-size: 0.8125rem;
      color: #64748b;
      font-weight: 500;
      
      strong {
        color: #2d1b69;
        font-weight: 700;
      }
    }
  }

  .pagination-compact {
    ::v-deep .pagination {
      margin-bottom: 0;
      gap: 4px;
      
      .page-item {
        .page-link {
          padding: 0;
          font-size: 0.875rem;
          line-height: 1;
          border-radius: 10px !important;
          margin: 0;
          border: 1.5px solid #e9e5ff;
          color: #5e5873;
          font-weight: 600;
          min-width: 38px;
          height: 38px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #fbfaff;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          
          &:hover {
            background: #f0edff;
            border-color: #7367f0;
            color: #7367f0;
            transform: translateY(-2px);
            box-shadow: 0 4px 10px rgba(115, 103, 240, 0.1);
          }

          &:focus {
            box-shadow: none;
          }
        }

        &.active .page-link {
          background: linear-gradient(135deg, #7367f0 0%, #9d7aff 100%) !important;
          border-color: #7367f0 !important;
          color: #fff !important;
          font-weight: 700;
          box-shadow: 0 4px 14px rgba(115, 103, 240, 0.4) !important;
          z-index: 1;
        }

        &.disabled .page-link {
          background: #f8fafc;
          border-color: #efebff;
          color: #b9b9c3;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        // Arrow styling
        &.prev-item, &.next-item, &.first-item, &.last-item {
          .page-link {
            font-size: 1.1rem;
            color: #b9b9c3;
            background: #f8f7ff;
          }
        }
      }
    }
  }

  .modern-table {
    ::v-deep th { background: #f8f8f8; font-size: 0.75rem; text-transform: uppercase; color: #5e5873; letter-spacing: 0.75px; border-top: none; padding: 1rem 0.75rem; }
    ::v-deep td { vertical-align: middle; padding: 1rem 0.75rem; border-bottom: 1px solid #f3f2f7; }
  }

  .action-btn-filter {
    border-radius: 10px;
    font-weight: 600;
    transition: all 0.3s ease;
    &:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(115, 103, 240, 0.2); }
  }

  .action-btn-table {
    width: 34px;
    height: 34px;
    padding: 0;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: 0.2s;
    &:hover { transform: translateY(-2px); box-shadow: 0 3px 8px rgba(115, 103, 240, 0.2); }
  }

  .action-btn-outline {
    width: 34px;
    height: 34px;
    padding: 0;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff !important;
    border: 1.5px solid;
    transition: 0.2s;
    
    &.delete-btn {
      border-color: #ef4444 !important;
      color: #ef4444 !important;
      &:hover { background: #fef2f2 !important; transform: translateY(-2px); }
    }

    &.warning-btn {
      border-color: #ff9f43 !important;
      color: #ff9f43 !important;
      &:hover { background: #fff5ed !important; transform: translateY(-2px); }
    }
  }

  .timeline-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-top: 4px;
    flex-shrink: 0;
  }

  .tracking-widest { letter-spacing: 0.1em; }
  .italic { font-style: italic; }
  .bg-light-faint { background-color: #fafbfc; }
  .border-right-light { border-right: 1px solid #ebe9f1; }

  .asset-avatar {
    box-shadow: 0 2px 8px rgba(115, 103, 240, 0.1);
  }

  .premium-sidebar {
    .sidebar-header-modern { height: 70px; background: #fff; border-bottom: 1px solid #ebe9f1; }
    .sidebar-icon-box { width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; }
  }

  .hover-translate-y:hover { transform: translateY(-2px); }

  .depreciation-modal-premium {
    .modal-content {
      border: none;
      border-radius: 16px;
      box-shadow: 0 10px 30px rgba(0,0,0,0.1);
    }
    
    .depreciation-sidebar {
      background: #fdfdff;
      
      .period-item {
        cursor: pointer;
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        border: 1.5px solid #f1f1f1;
        
        &:hover {
          transform: translateX(4px);
          border-color: #7367f0;
          box-shadow: 0 4px 12px rgba(115, 103, 240, 0.1);
        }
        
        &.bg-light-primary {
          border-color: #7367f0;
          box-shadow: 0 4px 12px rgba(115, 103, 240, 0.1);
        }
      }
    }

    .modern-table {
      th {
        background: #f8f9fe;
        color: #64748b;
        font-weight: 700;
        letter-spacing: 0.5px;
        border-bottom: 2px solid #edf2f7;
      }
      
      td {
        padding: 0.875rem;
        border-bottom: 1px solid #f1f5f9;
        vertical-align: middle;
      }
    }

    .schedule-table-card {
      border: 1px solid #edf2f7;
      background: #fff;
    }

    .max-w-400 { max-width: 400px; }
  }

  .action-card-modern {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    transition: all 0.3s ease;
    border-radius: 12px;
    
    &:hover {
      border-color: #cbd5e1;
      box-shadow: 0 4px 20px rgba(0,0,0,0.05);
    }
  }

  .action-btn-main {
    border-radius: 12px !important;
    font-weight: 700 !important;
    letter-spacing: 0.5px !important;
    height: 48px !important;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    
    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 15px rgba(40, 199, 111, 0.3) !important;
    }
  }

  .action-btn-secondary {
    border-radius: 12px !important;
    font-weight: 600 !important;
    height: 48px !important;
    color: #7367f0 !important;
    background-color: #f5f3ff !important;
    border: 1.5px solid #e2e8ff !important;
    transition: all 0.3s ease !important;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover:not(:disabled) {
      background-color: #ede9fe !important;
      border-color: #7367f0 !important;
      color: #5e50ee !important;
      transform: translateY(-1px);
    }
  }
}

::v-deep .vs__dropdown-toggle { border-radius: 8px; }
</style>
